import Events from '@utilities/events';
import InView from '@utilities/in-view';

import hasResponsiveImages from './util/detect-responsive-images';

const LAZY_IMAGE_HOOK = '.c-image';
const LAZY_SHADOW_IMAGE_HOOK = '[js-hook-shadow-image]';
const LAZY_IMAGE_SRC_HOOK = 'data-src';
const LAZY_IMAGE_SRCSET_HOOK = 'data-srcset';
const LAZY_IMAGE_ANIMATE_IN_CLASS = 'image--is-loaded';
const PD_EDIT_MODE_CLASS = '.is--page-designer-edit-mode';

const SUPPORTS_SRCSET = checkResponsiveImageRequirements();

class LazyImage {
  constructor() {
    this.images = getImageNodes(LAZY_IMAGE_HOOK);
    this.shadowImages = getImageNodes(LAZY_SHADOW_IMAGE_HOOK);

    this.bindEvents();
    this.setObserverables();
    this.setupDOMObserver();
  }

  bindEvents() {
    Events.$on('lazyimage::load', (e, element) => LazyImage.loadImage(element));
    Events.$on('lazyimage::update', () => this.updateImages());

    this.shadowImages.map((shadowImage) => LazyImage.removeShadowImage(shadowImage));
  }

  setObserverables() {
    InView.addElements(this.images, 'lazyimage::load');
  }

  /**
   * Setup MutationObserver to detect new images added to the DOM
   * This is needed for Page Designer components loaded dynamically
   */
  setupDOMObserver() {
    // Only run in Page Designer edit mode
    if (!isPageDesigner() || !window.MutationObserver) return;

    this.domObserver = new MutationObserver((mutations) => {
      let hasNewImages = false;

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            // Check if the added node is an image or contains images
            if (node.nodeType === 1) {
              const isImage = node.matches && node.matches(LAZY_IMAGE_HOOK);
              const hasImages = node.querySelectorAll && node.querySelectorAll(LAZY_IMAGE_HOOK).length > 0;

              if (isImage || hasImages) {
                hasNewImages = true;
              }
            }
          });
        }
      });

      // Update images if new ones were added
      if (hasNewImages) {
        this.updateImages();
      }
    });

    // Observe the entire document for added image nodes
    if (document.body) {
      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        this.domObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    }
  }

  static removeShadowImage(image) {
    image.addEventListener(
      'transitionend',
      () => {
        image.remove();
      },
      false,
    )
  }

  /**
   * Load the image
   * @param {HTMLElement} element Figure element
   */
  static loadImage(element) {
    const image = element.querySelector('img');
    if (!image) return;

    const src = image.getAttribute(LAZY_IMAGE_SRC_HOOK);
    const srcset = image.getAttribute(LAZY_IMAGE_SRCSET_HOOK);

    // If there is no data-src set just render the element.
    if (!src && image.src) {
      LazyImage.renderImage(element);
      return;
    }

    if (SUPPORTS_SRCSET && srcset) {
      image.srcset = srcset;
    }

    image.src = src;
    image.onload = () => LazyImage.renderImage(element);
  }

  /**
   * Set image source
   * @param {HTMLElement} element Figure element
   */
  static renderImage(element) {
    const image = element.querySelector('img');

    element.classList.add(LAZY_IMAGE_ANIMATE_IN_CLASS);
    image.removeAttribute(LAZY_IMAGE_SRC_HOOK);
    image.removeAttribute(LAZY_IMAGE_SRCSET_HOOK);

    if (!hasResponsiveImages.currentSrc) {
      image.currentSrc = image.src;
    }

    Events.$trigger('image::loaded', {
      data: image,
    });

    Events.$trigger('image::object-fit', {
      data: image,
    });
  }

  /**
   * Update new images
   */
  updateImages() {
    this.images = getImageNodes(LAZY_IMAGE_HOOK);
    this.images.forEach((image) => {

      const shadowImage = image.querySelector(LAZY_SHADOW_IMAGE_HOOK);

      if (shadowImage) {
        LazyImage.removeShadowImage(shadowImage);
      }
    })

    this.setObserverables();
  }
}

/**
 * Check if srcset is supported
 * @returns {Boolean}
 */
function checkResponsiveImageRequirements() {
  return hasResponsiveImages.srcset || hasResponsiveImages.picture;
}

/**
 * Check if srcset is supported
 * @param {String} selector Lookup identifier
 * @returns {Array}
 */
function getImageNodes(selector) {
  return [...document.querySelectorAll(selector)];
}

function isPageDesigner() {
  return Boolean(document.querySelector(PD_EDIT_MODE_CLASS));
}

export default new LazyImage();
